import {
  Box,
  Text,
  Flex,
  Button,
  Input,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Skeleton,
  useDisclosure,
} from '@chakra-ui/react';
import { MobileMenu } from './components/MobileMenu';
import { ApplicationRoutes } from 'utils/routes';
import { useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalContext } from '../../providers/Global';
import { Signer } from '../../types/account';
import { ReactComponent as DeleteIcon } from 'assets/delete-icon.svg';
import { ReactComponent as PlusIcon } from 'assets/plus-icon.svg';
import { ReactComponent as EditIcon } from 'assets/edit-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import DetailCard from './components/shared/DetailCard';
import DetailRow from './components/shared/DetailRow';
import { isValidAddress } from 'utils/general';
import { getAccountData } from '../../api/userAPI';
import { useToast } from 'hooks/useToast';
import { getWalletErrorMessage } from 'utils/walletErrors';
import ThresholdSettingsModal from 'components/Modals/ThresholdSettingsModal';
import { createThresholdUpdate, updateMultisigMetadata, getMultisigAccountData } from 'api/multisigAPI';
import { getNetworkDisplayName } from 'utils/networkConfigStore';
import { buildCustomStrategy } from '../../signing/useStrategy';
import { createThresholdUpdateScheduledTx } from '../../services/TransactionService';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import { clearKey } from '../../signing/KeyManager';
import copyToClipboard from 'copy-to-clipboard';
import { rotatedSigners, thresholdUpdateBody, withCurrentKeys } from '../../utils/rotatedSigners';

const SettingsPage = () => {
  const { walletId, multisigAccountId } = useParams();
  const {
    currentAccount,
    connection: { accountId: ownAccountId, hashconnectConnectorInstance },
    connectedNetwork,
    updateAccount,
  } = useContext(GlobalContext);
  const { showToast, generalSuccess } = useToast();
  const { requestSignConfirmation } = useSigningConfirmation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newWalletName, setNewWalletName] = useState('');
  const [newThreshold, setNewThreshold] = useState(1);
  const [newSigners, setNewSigners] = useState<Signer[]>([]);
  const [renamingIndex, setRenamingIndex] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [isRenamingWallet, setIsRenamingWallet] = useState(false);
  const [walletNameDraft, setWalletNameDraft] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  // The keys read for the review modal, so the update submits exactly what
  // the user confirmed there
  const [review, setReview] = useState<{
    signers: (Signer & { accountKey: string })[];
    rotated: { accountId: string; name?: string }[];
  } | null>(null);

  useEffect(() => {
    if (currentAccount) {
      setNewWalletName(currentAccount.name || '');
      if (currentAccount.keys) {
        setNewSigners(currentAccount.keys);
        setNewThreshold(currentAccount.threshold || 1);
      }
    }
  }, [currentAccount]);

  // --- Inline rename helpers ---

  const startRename = (index: number, currentName: string) => {
    setRenamingIndex(index);
    setRenameValue(currentName);
  };

  const cancelRename = () => {
    setRenamingIndex(null);
    setRenameValue('');
  };

  const startWalletRename = () => {
    setWalletNameDraft(currentAccount.name || '');
    setIsRenamingWallet(true);
  };

  const cancelWalletRename = () => {
    setIsRenamingWallet(false);
    setWalletNameDraft('');
  };

  const saveWalletRename = async () => {
    try {
      await updateMultisigMetadata(walletId!, {
        name: walletNameDraft,
        keys: (currentAccount.keys || []).map((s) => ({
          accountId: s.accountId,
          name: s.name || '',
        })),
      });
      const updatedData = await getMultisigAccountData(walletId!);
      if (updatedData?.data) {
        updateAccount(updatedData.data);
      }
      showToast('Wallet renamed', 'success', undefined, 'top');
    } catch (e) {
      console.error('Failed to rename wallet:', e);
      showToast('Failed to rename wallet', 'error', undefined, 'top');
    } finally {
      setIsRenamingWallet(false);
      setWalletNameDraft('');
    }
  };

  const saveRename = async (index: number) => {
    const signer = currentAccount.keys?.[index];
    if (!signer) return;
    try {
      await updateMultisigMetadata(walletId!, {
        name: currentAccount.name || '',
        keys: (currentAccount.keys || []).map((s, i) => ({
          accountId: s.accountId,
          name: i === index ? renameValue : (s.name || ''),
        })),
      });
      const updatedData = await getMultisigAccountData(walletId!);
      if (updatedData?.data) {
        updateAccount(updatedData.data);
      }
      showToast('Member renamed', 'success', undefined, 'top');
    } catch (e) {
      console.error('Failed to rename member:', e);
      showToast('Failed to rename member', 'error', undefined, 'top');
    } finally {
      setRenamingIndex(null);
      setRenameValue('');
    }
  };

  // --- Edit config helpers ---

  const handleAddSigner = () => {
    setNewSigners([
      ...newSigners,
      { name: '', accountId: '', accountKey: '' },
    ]);
  };

  const handleRemoveSigner = (index: number) => {
    const updated = [...newSigners];
    updated.splice(index, 1);
    setNewSigners(updated);
    if (newThreshold > updated.length) {
      setNewThreshold(Math.max(1, updated.length));
    }
  };

  const handleSignerChange = (index: number, field: keyof Signer, value: string) => {
    const updated = [...newSigners];
    updated[index] = { ...updated[index], [field]: value };
    setNewSigners(updated);
  };

  const { isValid, isMetadataOnly, isThresholdChanged, isSignerConfigChanged } = useMemo(() => {
    if (newSigners.length === 0) return { isValid: false, isMetadataOnly: false, isThresholdChanged: false, isSignerConfigChanged: false };
    if (newThreshold > newSigners.length || newThreshold < 1) return { isValid: false, isMetadataOnly: false, isThresholdChanged: false, isSignerConfigChanged: false };

    const addresses = new Set();
    for (const signer of newSigners) {
      if (!isValidAddress(signer.accountId)) return { isValid: false, isMetadataOnly: false, isThresholdChanged: false, isSignerConfigChanged: false };
      if (addresses.has(signer.accountId)) return { isValid: false, isMetadataOnly: false, isThresholdChanged: false, isSignerConfigChanged: false };
      addresses.add(signer.accountId);
    }

    const thresholdChanged = newThreshold !== (currentAccount.threshold || 0);
    const signersCountChanged = newSigners.length !== (currentAccount.keys?.length || 0);
    const signerAccountsChanged = newSigners.some((signer, index) => {
      const original = currentAccount.keys?.[index];
      return !original || signer.accountId !== original.accountId;
    });

    const configChanged = thresholdChanged || signersCountChanged || signerAccountsChanged;

    return {
      isValid: true,
      isMetadataOnly: !configChanged,
      isThresholdChanged: thresholdChanged,
      isSignerConfigChanged: signersCountChanged || signerAccountsChanged
    };
  }, [newSigners, newThreshold, currentAccount, newWalletName]);

  const { addedSigners, removedSigners } = useMemo(() => {
    const currentIds = new Set((currentAccount.keys || []).map((k: any) => k.accountId));
    const newIds = new Set(newSigners.map((s) => s.accountId));
    return {
      addedSigners: newSigners
        .filter((s) => !currentIds.has(s.accountId))
        .map((s) => ({ name: s.name, accountId: s.accountId })),
      removedSigners: (currentAccount.keys || [])
        .filter((k: any) => !newIds.has(k.accountId))
        .map((k: any) => ({ name: k.name || '', accountId: k.accountId })),
    };
  }, [newSigners, currentAccount]);

  const handleUpdate = async () => {
    try {
      setIsLoading(true);

      if (isMetadataOnly) {
        await updateMultisigMetadata(walletId!, {
          name: newWalletName,
          keys: newSigners.map(s => ({
            accountId: s.accountId,
            name: s.name || '',
          })),
        });
        const updatedData = await getMultisigAccountData(walletId!);
        if (updatedData?.data) {
          updateAccount(updatedData.data);
        }
        setIsEditMode(false);
        return;
      }

      if (!review) return;
      const { signers: signersWithKeys, rotated } = review;
      const publicKeys = signersWithKeys.map((s) => s.accountKey);
      const rotatedNames = rotated.map((s) => s.name || s.accountId).join(', ');

      const isCustom = connectedNetwork?.startsWith('custom-');

      if (isCustom) {
        const confirmed = await requestSignConfirmation({
          type: 'Update Configuration',
          items: [
            { label: 'Wallet', value: multisigAccountId! },
            { label: 'New Threshold', value: `${newThreshold} of ${newSigners.length} signers` },
            ...(rotated.length ? [{ label: 'Key change', value: `${rotatedNames}: new key on Hedera` }] : []),
          ],
        });
        if (!confirmed) {
          setIsLoading(false);
          return;
        }
      }

      // Build strategy AFTER confirmation (key is freshly decrypted)
      const strategy = buildCustomStrategy(connectedNetwork, ownAccountId!);

      let response;
      if (strategy) {
        response = await createThresholdUpdateScheduledTx(
          strategy,
          multisigAccountId!,
          publicKeys,
          newThreshold,
          multisigAccountId!,
        );
      } else {
        response = await hashconnectConnectorInstance.createThresholdUpdateScheduledTransaction(
          multisigAccountId!,
          publicKeys,
          newThreshold,
          multisigAccountId!,
          connectedNetwork!,
          ownAccountId
        );
      }

      const updateData = thresholdUpdateBody(response, newThreshold, signersWithKeys, isSignerConfigChanged, rotated);

      await createThresholdUpdate(walletId!, updateData);

      generalSuccess();
      setIsEditMode(false);
      closeReview();
    } catch (error: any) {
      console.error(error);
      showToast(
        getWalletErrorMessage(error, 'Failed to update. Transaction rejected or API error.'),
        'error',
        undefined,
        'top-right',
      );
    } finally {
      clearKey();
      setIsLoading(false);
    }
  };

  // Keys are re-read from the mirror, so a signer who rotated their own
  // account key since the wallet was set up gets the new key here. The
  // update then changes that key too: the review modal says so before
  // anything is signed, and the full list goes to the backend.
  const openReview = async () => {
    setIsLoading(true);
    try {
      const signers = await withCurrentKeys(newSigners, async (id) => (await getAccountData(id, connectedNetwork!)).key);
      setReview({ signers, rotated: rotatedSigners(currentAccount.keys || [], signers) });
      onOpen();
    } catch (error: any) {
      console.error(error);
      showToast(error?.message || 'Failed to read the signer keys', 'error', undefined, 'top-right');
    } finally {
      setIsLoading(false);
    }
  };

  const closeReview = () => {
    onClose();
    setReview(null);
  };

  const enterEditMode = () => {
    setNewWalletName(currentAccount.name || '');
    setNewSigners(currentAccount.keys || []);
    setNewThreshold(currentAccount.threshold || 1);
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setIsEditMode(false);
    setNewWalletName(currentAccount.name || '');
    setNewSigners(currentAccount.keys || []);
    setNewThreshold(currentAccount.threshold || 1);
  };

  // --- Render ---
  return (
    <Flex flex={1} flexDirection="column" gap={4}>
      <MobileMenu
        pageName="Settings"
        backUrl={`${ApplicationRoutes.Dashboard}/${walletId}/account/${multisigAccountId}`}
      />

      {/* Wallet Details */}
      <Box
        p={6}
        bg="white"
        borderRadius={16}
        boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
      >
        <Text size="lg" fontWeight={300} color="blackAlpha.500" mb={4}>
          Wallet Details
        </Text>

        <DetailCard>
          <DetailRow
            label="Name"
            value={
              isRenamingWallet ? (
                <Flex gap={2} alignItems="center">
                  <Input
                    size="sm"
                    value={walletNameDraft}
                    onChange={(e) => setWalletNameDraft(e.target.value)}
                    borderRadius={6}
                    borderWidth={1}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveWalletRename();
                      if (e.key === 'Escape') cancelWalletRename();
                    }}
                  />
                  <Button size="xs" variant="primary" borderRadius={6} onClick={saveWalletRename}>
                    Save
                  </Button>
                  <Button size="xs" variant="ghost" borderRadius={6} onClick={cancelWalletRename}>
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Text size="sm" variant="bodyBold">
                  {currentAccount.name || 'Unnamed Wallet'}
                </Text>
              )
            }
            action={
              !isRenamingWallet ? (
                <IconButton
                  aria-label="Rename wallet"
                  icon={<EditIcon width={14} height={14} />}
                  variant="ghost"
                  size="xs"
                  onClick={startWalletRename}
                />
              ) : undefined
            }
          />
          <DetailRow
            label="Address"
            value={multisigAccountId || ''}
            wordBreak="break-all"
            onCopy={() => {
              copyToClipboard(multisigAccountId || '');
              showToast('Copied to clipboard', 'success', undefined, 'top');
            }}
          />
          <DetailRow
            label="Network"
            value={
              <Text size="sm" variant="bodyBold" textTransform="capitalize">
                {getNetworkDisplayName(currentAccount?.network || '') || '—'}
              </Text>
            }
          />
        </DetailCard>
      </Box>

      {/* Configuration */}
      <Box
        p={6}
        bg="white"
        borderRadius={16}
        boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text size="lg" fontWeight={300} color="blackAlpha.500">
            Configuration
          </Text>
          {!isEditMode && (
            <Button
              variant="outline"
              borderRadius={6}
              size="sm"
              onClick={enterEditMode}
            >
              Edit Configuration
            </Button>
          )}
        </Flex>

        {isEditMode ? (
          <>
            {/* Edit: Members */}
            <Text size="md" variant="bodyBold" mb={3}>Members</Text>
            <Flex flexDirection="column" gap={2} mb={4}>
              {newSigners.map((signer, index) => (
                <Flex
                  key={index}
                  gap={3}
                  p={3}
                  bg="#FAFAFA"
                  borderRadius={10}
                  alignItems="center"
                >
                  <Flex flex={1} gap={4} direction={{ base: 'column', md: 'row' }}>
                    <Input
                      placeholder="Name (optional)"
                      value={signer.name}
                      onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
                      bg="white"
                      borderRadius={8}
                      borderWidth={2}
                    />
                    <Input
                      placeholder="Account ID (0.0.xxxxx)"
                      value={signer.accountId}
                      isInvalid={
                        signer.accountId !== '' &&
                        (!isValidAddress(signer.accountId) ||
                          newSigners.filter(s => s.accountId === signer.accountId).length > 1)
                      }
                      onChange={(e) => handleSignerChange(index, 'accountId', e.target.value)}
                      bg="white"
                      borderRadius={8}
                      borderWidth={2}
                    />
                  </Flex>
                  {newSigners.length > 1 && (
                    <IconButton
                      aria-label="Remove member"
                      icon={<DeleteIcon />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleRemoveSigner(index)}
                    />
                  )}
                </Flex>
              ))}
            </Flex>
            <Button
              size="sm"
              variant="outline"
              borderRadius={6}
              leftIcon={<PlusIcon />}
              onClick={handleAddSigner}
              mb={6}
            >
              Add Member
            </Button>

            {/* Edit: Required Signatures */}
            <Text size="md" variant="bodyBold" mb={3}>Required Signatures</Text>
            <Flex alignItems="center" gap={4} mb={6}>
              <NumberInput
                min={1}
                max={newSigners.length}
                value={newThreshold}
                onChange={(_, val) => setNewThreshold(val)}
                maxWidth="100px"
              >
                <NumberInputField borderRadius={8} borderWidth={2} />
                <NumberInputStepper>
                  <NumberIncrementStepper border="none" />
                  <NumberDecrementStepper border="none" />
                </NumberInputStepper>
              </NumberInput>
              <Text>of {newSigners.length} members</Text>
            </Flex>

            {/* Actions */}
            <Flex gap={3} justifyContent="flex-end">
              <Button
                variant="secondary"
                borderRadius={6}
                size="sm"
                onClick={cancelEditMode}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                borderRadius={6}
                size="sm"
                isDisabled={!isValid}
                isLoading={isLoading}
                onClick={() => {
                  if (isMetadataOnly) {
                    handleUpdate();
                  } else {
                    openReview();
                  }
                }}
              >
                {isMetadataOnly ? 'Save Changes' : 'Review Changes'}
              </Button>
            </Flex>
          </>
        ) : (
          <>
            {/* Read: Members */}
            <Text size="md" variant="bodyBold" mb={3}>
              Members ({currentAccount.keys?.length || 0})
            </Text>
            <Flex flexDirection="column" gap={0} mb={6}>
              {currentAccount?.keys?.map((signer: Signer, index: number) => {
                const isRenaming = renamingIndex === index;
                const displayName = signer.name || 'Unnamed Member';

                return (
                  <Flex
                    key={index}
                    gap={3}
                    py={3}
                    px={4}
                    borderRadius={8}
                    alignItems="center"
                    borderBottom={index < (currentAccount.keys?.length || 0) - 1 ? '1px solid' : 'none'}
                    borderColor="blackAlpha.100"
                    _hover={{ bg: '#FAFAFA' }}
                    transition="background 0.15s"
                  >
                    <Box flex={1} minW={0}>
                      {isRenaming ? (
                        <Flex gap={2} alignItems="center">
                          <Input
                            size="sm"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            borderRadius={6}
                            borderWidth={1}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRename(index);
                              if (e.key === 'Escape') cancelRename();
                            }}
                          />
                          <Button size="xs" variant="primary" borderRadius={6} onClick={() => saveRename(index)}>
                            Save
                          </Button>
                          <Button size="xs" variant="ghost" borderRadius={6} onClick={cancelRename}>
                            Cancel
                          </Button>
                        </Flex>
                      ) : (
                        <>
                          <Flex alignItems="center" gap={1}>
                            <Text size="sm" variant="bodyBold" isTruncated>
                              {displayName}
                            </Text>
                            <IconButton
                              aria-label="Rename member"
                              icon={<EditIcon width={12} height={12} />}
                              variant="ghost"
                              size="xs"
                              h={6}
                              minW={6}
                              onClick={() => startRename(index, signer.name || '')}
                            />
                          </Flex>
                          <Flex alignItems="center" gap={1} mt={0.5}>
                            <Text size="xs" color="blackAlpha.500" fontFamily="mono">
                              {signer.accountId}
                            </Text>
                            <Box
                              as="button"
                              aria-label={`Copy address ${signer.accountId}`}
                              display="inline-flex"
                              onClick={() => {
                                copyToClipboard(signer.accountId);
                                showToast('Copied to clipboard', 'success', undefined, 'top');
                              }}
                            >
                              <CopyIcon width={14} height={14} />
                            </Box>
                          </Flex>
                        </>
                      )}
                    </Box>
                  </Flex>
                );
              })}
            </Flex>

            {/* Read: Required Signatures */}
            <Text size="md" variant="bodyBold" mb={3}>
              Required Signatures
            </Text>
            <Box p={4} bg="primary" borderRadius={8}>
              <Skeleton isLoaded={!!currentAccount?.threshold} borderRadius={6} minW="80px" minH="24px">
                <Text size="lg" variant="bodyBold">
                  {currentAccount?.threshold} of {currentAccount?.keys?.length}
                </Text>
              </Skeleton>
              <Text size="xs" color="blackAlpha.500" mt={1}>
                Transactions require {currentAccount?.threshold} out of {currentAccount?.keys?.length} members to sign
              </Text>
            </Box>
          </>
        )}
      </Box>

      <ThresholdSettingsModal
        data={{
          open: isOpen,
          isLoading,
          current: {
            threshold: currentAccount.threshold || 0,
            signersCount: currentAccount.keys?.length || 0,
          },
          next: {
            threshold: newThreshold,
            signersCount: newSigners.length,
          },
          create: handleUpdate,
          handleClose: closeReview,
          addedSigners,
          removedSigners,
          rotatedSigners: review?.rotated,
        }}
      />
    </Flex>
  );
};

export default SettingsPage;
